// Feature visibility — SINGLE SOURCE OF TRUTH.
//
// To change where a page/feature appears, edit ONLY this file. Consumers read
// from here, so a change here takes effect everywhere at once:
//   • the sidebar menu          (layout/components/Sidebar/RouterMenu.vue)
//   • the global route guard     (router/index.js — blocks direct-URL access)
//   • environment-gated UI bits  (isFeatureEnabled — e.g. staging-only tabs)
//   • server-menu-gated UI bits  (isMenuItemEnabled — e.g. Settings' Goals /
//     Notifications tabs, driven by the current hotel's /auth/entities menu)
//
// env.js holds the low-level environment primitives (isStaging / isChatEnabled);
// this file is the policy layer that decides what those primitives gate.

import { isStaging, isChatEnabled } from "./env";
import { flagOn } from "./analytics";
import { DEMO_HOTEL_NAME_MATCH } from "@/config/demoAccount";
// 只有酒店名片段和按需 import()，不会把报告 JSON 带进主包
import { LUXURY_STANDARDS_LOCAL_HOTELS, matchesHotelName } from "@/views/QualityStandards/reports/index.js";

// ── Route paths that are NOT customer-facing yet ────────────────────────────
// Shown on staging/localhost, hidden on prod. Child paths are covered by prefix
// (e.g. "/Survey" also gates "/Survey/PostStay"). To put one of these live on
// prod, just delete it from this list.
export const STAGING_ONLY_PATHS = ["/Survey", "/BrandOverview", "/StaffManagement"];

// ── Routes that only exist for SPECIFIC hotels ──────────────────────────────
// path → a fragment (or a list of fragments, any one matching is enough) that must
// appear in the current hotel's name (case-insensitive "includes", same loose matching
// as config/demoAccount.js and for the same reason: the backend name may carry a
// branch/city suffix, so an exact match is too brittle).
//
// These pages ship with data prepared for one hotel (or one brand) only — opening
// them anywhere else would just render an empty page, so the sidebar hides the entry
// AND the route guard blocks the direct URL. Child paths are covered by prefix.
export const HOTEL_ONLY_PATHS = {
  // 半年报：Kimpton 是完整版；演示酒店 Grand Luxury 只有解决方案矩阵（2026-10-02，
  // 见 HalfYearReview/halfYearData.js 的 REPORT_FILES）
  // The Sanya EDITION（2026-10-12）：半年报 + 竞对半年报都是 mock 渲染包（18 周版）
  // W Shanghai - The Bund（2026-10-12）：同上，40 周版，中英两版
  // Radisson RED Vienna（2026-10-22 v2_9）：半年报 + 竞对半年报，25 周，只有英文版。数组 = 几个片段都要命中，
  // 只写 "Radisson RED" 的话别的城市的 Radisson RED 也会出菜单
  // InterContinental Bangkok / Hua Hin / Phuket（2026-11-03）：半年报；竞对半年报只有 Bangkok。
  // "!" 开头的片段 = 名字里不能有它：只写 "InterContinental Bangkok" 的话 InterContinental Bangkok Sukhumvit 也会出菜单
  "/HalfYearReview": [
    "Kimpton Maa-Lai Bangkok", DEMO_HOTEL_NAME_MATCH, "Sanya EDITION", "W Shanghai", ["Radisson RED", "Vienna"],
    ["InterContinental Bangkok", "!Sukhumvit"], "InterContinental Hua Hin", "InterContinental Phuket",
  ],
  "/MarketHalfYear": [
    "Kimpton Maa-Lai Bangkok", "Sanya EDITION", "W Shanghai", ["Radisson RED", "Vienna"],
    ["InterContinental Bangkok", "!Sukhumvit"],
  ],
  // 质量标准：不看接口返回的 menu（MENU_TYPE_TO_PATH 里也没有它），只按酒店名放行。
  // 页面分两版（见下面的 LUXURY_STANDARDS_HOTELS）：Kimpton 看原来的 LQA / Forbes / Michelin 版，
  // 有本地报告的酒店（QualityStandards/reports/index.js 登记；正式环境目前是 W Shanghai、InterContinental Bangkok、InterContinental Phuket）看新版 WTK Luxury Standards。
  // 演示酒店 Grand Luxury 在正式环境仍看原来的版本。
  "/QualityStandards": ["Kimpton Maa-Lai Bangkok", DEMO_HOTEL_NAME_MATCH, ...LUXURY_STANDARDS_LOCAL_HOTELS],
};

// ── Routes that only exist for accounts owning a SPECIFIC restaurant ────────
// path → the exact hotelName (case-insensitive, trimmed) that the CURRENTLY SELECTED
// restaurant (`restaurant/currentRestaurant`, an entry of /auth/entities `restaurants`)
// must carry. Switching to another restaurant of the same account hides the menu and
// the route guard blocks the direct URL. Child paths are covered by prefix.
export const RESTAURANT_ENTITY_ONLY_PATHS = {
  "/FnbHalfYear": "Kimpton Maa-Lai Bangkok",
};

// ── Routes that belong to the RESTAURANT module only ─────────────────────────
// 这些页面只在餐厅模式下有意义（数据是餐厅的）。酒店模式下（hotel.isHotel=true、
// 非品牌模式）不管是切回酒店、登录后恢复「上次访问地址」还是直接输 URL，都不该停在
// 这里，否则会出现「酒店侧边栏 + 餐厅半年报」的错位。路由守卫读这张表把它挡回 /index。
// Child paths are covered by prefix.
export const RESTAURANT_MODULE_ONLY_PATHS = ["/FnbHalfYear"];

// True if `path` is a restaurant-module-only page (see RESTAURANT_MODULE_ONLY_PATHS).
export function isRestaurantModuleOnlyPath(path) {
  if (!path) return false;
  return RESTAURANT_MODULE_ONLY_PATHS.some((p) => matchesPrefix(path, p));
}

// ── Routes gated by a NAMED feature flag (not just staging) ──────────────────
// path → feature key in FEATURE_GATES below.
const PATH_FEATURE = {
  "/chat": "aiChat",
};

// ── Named feature gates (for routes above AND non-route UI like tabs) ────────
// Each value is a predicate returning true when the feature is enabled here.
const FEATURE_GATES = {
  aiChat: isChatEnabled,        // AI Chat / Intelligence page
};

// ── Server-side menu type → route path mapping (per module) ─────────────────
// Both modules' server menus reuse the same `type` values (e.g. OVERVIEW,
// REVIEWS) for conceptually-equivalent-but-differently-routed pages, so each
// module needs its own type → path table.
export const MENU_TYPE_TO_PATH = {
  AI_CHAT: "/chat",
  OVERVIEW: "/index",
  REVIEWS: "/reviews",
  CASE_MANAGEMENT: "/NeedsAttention",
  REPLY_SETTING: "/ReplySetting",
  REPLY_LIBRARY: "/ReplyLibrary",
  MARKET_OVERVIEW: "/competitors/overview",
  COMPETITOR_BENCHMARK: "/competitors/analysis",
  REPORTS: "/Reports",
  BRAND_OVERVIEW: "/BrandOverview",
  GROUP_OVERVIEW: "/BrandOverview2",
  DURING_STAY: "/Survey/DuringStay",
  POST_STAY: "/Survey/PostStay",
  SURVEY_DETAIL: "/Survey/Detail",
  SETTING: "/setting",
  HELP_CENTER: "/FAQ",
  STAFF_MANAGEMENT: "/StaffManagement",
};

// Restaurant-mode equivalent — same server `type` vocabulary, different routes.
export const RESTAURANT_MENU_TYPE_TO_PATH = {
  AI_CHAT: "/chat",
  OVERVIEW: "/RestaurantDashboard",
  REVIEWS: "/RestaurantReviewsIndex",
  CASE_MANAGEMENT: "/NeedsAttention",
  REPLY_SETTING: "/ReplySetting",
  REPLY_LIBRARY: "/ReplyLibrary",
  MARKET_OVERVIEW: "/competitors/overview",
  COMPETITOR_BENCHMARK: "/competitors/analysis",
  REPORTS: "/Reports",
  PORTFOLIO_BRIEFING: "/PortfolioBriefing",
  SETTING: "/setting",
  HELP_CENTER: "/FAQ",
  STAFF_MANAGEMENT: "/StaffManagement",
};

function invert(map) {
  return Object.fromEntries(Object.entries(map).map(([type, path]) => [path, type]));
}

const PATH_TO_MENU_TYPE = invert(MENU_TYPE_TO_PATH);
const RESTAURANT_PATH_TO_MENU_TYPE = invert(RESTAURANT_MENU_TYPE_TO_PATH);

function matchesPrefix(path, prefix) {
  return path === prefix || path.startsWith(prefix + "/");
}

// True if a named (non-route) feature is enabled in the current environment.
// Unknown names default to enabled, so a typo never hides a shipped feature.
export function isFeatureEnabled(name) {
  const gate = FEATURE_GATES[name];
  return gate ? gate() : true;
}

// True if `path` is allowed (visible / navigable) in the current environment.
// Used by both the route guard and the sidebar so they can never disagree.
export function isPathEnabled(path) {
  if (!path) return true;
  for (const prefix of Object.keys(PATH_FEATURE)) {
    if (matchesPrefix(path, prefix)) return isFeatureEnabled(PATH_FEATURE[prefix]);
  }
  if (STAGING_ONLY_PATHS.some((p) => matchesPrefix(path, p))) return isStaging();
  return true;
}

// True if `path` is allowed for the hotel currently selected (see HOTEL_ONLY_PATHS).
// Separate from isPathEnabled because that one is a pure environment check with no
// arguments, while this needs the caller to pass the hotel it is asking about —
// this file deliberately doesn't reach into the store.
//
// hotelName: `hotel/currentHotel`'s hotelName (may be empty/undefined — a path that
// is hotel-gated then stays hidden, which is the safe default while the hotel list
// is still loading).
// currentRestaurant: `restaurant/currentRestaurant` (the selected entry of the
// /auth/entities `restaurants`), checked for RESTAURANT_ENTITY_ONLY_PATHS; none
// selected / list not loaded -> those paths stay hidden.
// /QualityStandards 下看新版 WTK Luxury Standards 的酒店（按酒店名片段匹配，同 HOTEL_ONLY_PATHS）。
// 不在这里的已开放酒店仍然看原来的 LQA / Forbes / Michelin 版。
// 有本地报告的酒店在 QualityStandards/reports/index.js 登记一次，这里和上面的菜单放行自动跟上。
export const LUXURY_STANDARDS_HOTELS = [...LUXURY_STANDARDS_LOCAL_HOTELS];

export function isLuxuryStandardsHotel(hotelName) {
  return LUXURY_STANDARDS_HOTELS.some((match) => matchesHotelName(match, hotelName));
}

export function isPathEnabledForHotel(path, hotelName, currentRestaurant) {
  if (!path) return true;
  const restaurantGated = Object.keys(RESTAURANT_ENTITY_ONLY_PATHS).find((p) => matchesPrefix(path, p));
  if (restaurantGated) {
    const want = RESTAURANT_ENTITY_ONLY_PATHS[restaurantGated].trim().toLowerCase();
    return !!currentRestaurant && String(currentRestaurant.hotelName || "").trim().toLowerCase() === want;
  }
  const gated = Object.keys(HOTEL_ONLY_PATHS).find((p) => matchesPrefix(path, p));
  if (!gated) return true;
  const name = String(hotelName || "").toLowerCase();
  // 每一项是一个片段，或一组都要命中的片段（如 ["Radisson RED", "Vienna"]）；"!" 开头的片段是名字里不能有的
  const hits = (frag) => {
    const f = String(frag).toLowerCase();
    return f.startsWith("!") ? !name.includes(f.slice(1)) : name.includes(f);
  };
  return [].concat(HOTEL_ONLY_PATHS[gated]).some((frag) => (Array.isArray(frag) ? frag.every(hits) : hits(frag)));
}

// Server-menu types that default to HIDDEN when absent from the account's menu data,
// instead of the usual "not present = no restriction" fallback below — an opt-in
// allowlist for features still being rolled out to specific accounts only. Add a type
// here when it should stay invisible until the backend explicitly grants it.
// CONNECTIONS (the Connections tab of Settings): the backend sends it switched off until
// admin enables it for the unit.
const DEFAULT_DISABLED_MENU_TYPES = new Set(["GROUP_OVERVIEW", "CONNECTIONS"]);

// True if a server-menu item `type` is enabled, searching all groups of
// `moduleMenu` (the `menu` object from the hotel/restaurant API). Type values
// are unique across groups, so no group filtering is needed.
function isMenuTypeEnabled(moduleMenu, type) {
  const defaultWhenAbsent = !DEFAULT_DISABLED_MENU_TYPES.has(type);
  if (!type || !moduleMenu || !moduleMenu.groups) return defaultWhenAbsent;
  for (const group of moduleMenu.groups) {
    const item = (group.items || []).find((i) => i.type === type);
    if (item) return item.enabled !== false;
  }
  return defaultWhenAbsent;
}

function checkMenuPathEnabled(path, moduleMenu, pathToType) {
  if (!path) return true;
  return isMenuTypeEnabled(moduleMenu, pathToType[path]);
}

// True if `path` is enabled according to the current hotel's server-side menu.
// hotelMenu is the `menu` object from the hotels API (hotel.menu).
// Paths not present in the API mapping are always considered enabled (no server gate).
// If hotelMenu is null/undefined (data not yet loaded), defaults to enabled.
export function isMenuPathEnabled(path, hotelMenu) {
  return checkMenuPathEnabled(path, hotelMenu, PATH_TO_MENU_TYPE);
}

// Restaurant-mode equivalent of isMenuPathEnabled() — restaurantMenu is the
// `menu` object from the restaurant API (same groups/items shape, restaurant
// route paths via RESTAURANT_MENU_TYPE_TO_PATH).
export function isRestaurantMenuPathEnabled(path, restaurantMenu) {
  return checkMenuPathEnabled(path, restaurantMenu, RESTAURANT_PATH_TO_MENU_TYPE);
}

// True if a named server-menu item (e.g. "SETTING_GOALS") is enabled — for UI
// that isn't a standalone route, like Settings' Goals/Notifications tabs.
// `groupKey` (e.g. "SETTING") is accepted for call-site readability only;
// items are matched by `type` alone.
export function isMenuItemEnabled(moduleMenu, groupKey, type) {
  return isMenuTypeEnabled(moduleMenu, type);
}
// ── Feature flags (PostHog, WTK-728) ─────────────────────────────────────────
// A feature shown only to whom its flag is on for (people, properties, a percentage), switched in
// PostHog without a release. Pass a key declared in config/featureFlags.js: isFeatureOn(FLAGS.X).
// A flag only hides or shows; anything that needs permission is still checked by the api.
export function isFeatureOn(flag) {
  return flagOn(flag);
}
